import { normalizeDate } from "@features/vessel-call/vessel-call-status/PortServices/flow/utils";
import type { TFunction } from "i18next";
import { z } from "zod";

const LANG_BASE = "vessel-call.port-services";
const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/;

type ShipChandlingData = {
  serviceType?: string;
  direction?: string;
  rts?: string | Date | null;
  numberOfPackagesHandled?: string;
  additionalResources?: Array<{
    resourceType?: string;
    numberOfResources?: string;
  }>;
  receivingVesselImoNumber?: string;
  receivingVesselCallId?: string;
  receivingVesselName?: string;
  receivingVesselLookupPending?: boolean;
  customsDocuments?: Array<unknown>;
  remarks?: string;
};

const addRequiredIssue = (
  ctx: z.RefinementCtx,
  t: TFunction,
  path: string | (string | number)[],
  labelKey: string,
) => {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: t("form-validation.required", {
      label: t(`${LANG_BASE}.fields.${labelKey}`),
    }),
    path: Array.isArray(path) ? path : [path],
  });
};

const addInvalidNumberIssue = (
  ctx: z.RefinementCtx,
  t: TFunction,
  path: string | (string | number)[],
  labelKey: string,
) => {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: t("form-validation.invalid-number", {
      label: t(`${LANG_BASE}.fields.${labelKey}`),
    }),
    path: Array.isArray(path) ? path : [path],
  });
};

export const validateShipChandlingServices = (
  data: ShipChandlingData,
  ctx: z.RefinementCtx,
  t: TFunction,
) => {
  const normalizedReceivingVesselImoNumber = data.receivingVesselImoNumber?.trim();

  if (!data.direction?.trim()) {
    addRequiredIssue(ctx, t, "direction", "ship-chandling-direction");
  }

  const normalizedRts = normalizeDate(data.rts);
  if (!normalizedRts) {
    addRequiredIssue(ctx, t, "rts", "rts");
  } else if (normalizedRts < new Date()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.must-not-be-in-past", {
        label: t(`${LANG_BASE}.fields.rts`),
      }),
      path: ["rts"],
    });
  }

  if (!data.numberOfPackagesHandled?.trim()) {
    addRequiredIssue(ctx, t, "numberOfPackagesHandled", "number-of-packages-handled");
  } else if (!POSITIVE_INTEGER_PATTERN.test(data.numberOfPackagesHandled.trim())) {
    addInvalidNumberIssue(ctx, t, "numberOfPackagesHandled", "number-of-packages-handled");
  }

  if (!data.additionalResources?.length) {
    addRequiredIssue(ctx, t, "additionalResources", "additional-resources-equipment");
  } else {
    data.additionalResources.forEach((resource, index) => {
      if (!resource.resourceType?.trim()) {
        addRequiredIssue(
          ctx,
          t,
          ["additionalResources", index, "resourceType"],
          "resources-equipment",
        );
      }

      if (!resource.numberOfResources?.trim()) {
        addRequiredIssue(
          ctx,
          t,
          ["additionalResources", index, "numberOfResources"],
          "number-of-resources-equipment",
        );
      } else {
        const label = t(`${LANG_BASE}.fields.number-of-resources-equipment`);
        const trimmedValue = resource.numberOfResources.trim();

        if (trimmedValue.includes(".") || trimmedValue.includes(",")) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${label} must be an integer (no decimals).`,
            path: ["additionalResources", index, "numberOfResources"],
          });
          return;
        }

        const parsedValue = parseInt(trimmedValue, 10);
        const isInteger = !Number.isNaN(parsedValue) && parsedValue.toString() === trimmedValue;

        if (!isInteger) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${label} must be an integer.`,
            path: ["additionalResources", index, "numberOfResources"],
          });
          return;
        }

        if (parsedValue < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${label} must be a positive number.`,
            path: ["additionalResources", index, "numberOfResources"],
          });
          return;
        }

        if (trimmedValue.length > 4) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-have-maximum-digits", {
              label,
              max: 4,
            }),
            path: ["additionalResources", index, "numberOfResources"],
          });
        }
      }
    });
  }

  if (!normalizedReceivingVesselImoNumber) {
    addRequiredIssue(ctx, t, "receivingVesselImoNumber", "receiving-vessel-imo-number");
  } else if (!/^\d+$/.test(normalizedReceivingVesselImoNumber)) {
    addInvalidNumberIssue(ctx, t, "receivingVesselImoNumber", "receiving-vessel-imo-number");
  } else if (!data.receivingVesselCallId?.trim() && data.receivingVesselLookupPending !== true) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t(`${LANG_BASE}.fields.receiving-vessel-validation-required`),
      path: ["receivingVesselImoNumber"],
    });
  }

  if (!data.receivingVesselName?.trim()) {
    addRequiredIssue(ctx, t, "receivingVesselName", "receiving-vessel-name");
  }

  if (data.direction === "Outbound" && !(data.customsDocuments?.length ?? 0)) {
    addRequiredIssue(ctx, t, "customsDocuments", "customs-document");
  }
};
